/**
 * Project Conversion Utilities
 *
 * Converts ProjectData (workflow store format) to Project (calculation engine format)
 */

import type { ProjectData } from '~/hooks/useProjectStore';
import type { Project, ProjectDateElements } from '~/types';

/**
 * Convert ProjectData to Project format for builders
 *
 * Bridges workflow store format to calculation engine format
 *
 * @param projectData - ProjectData from workflow store
 * @param requireDateMaps - If true, validates that date maps exist (for processing). If false, allows missing maps (for display)
 */
export const convertProjectDataToProject = (
  projectData: ProjectData,
  requireDateMaps: boolean = false
): Project => {
  // Validate date maps if required (for processing operations)
  if (requireDateMaps) {
    if (!projectData.dateElements?.dateRecordsMapByPaymentDate) {
      throw new Error(
        'Date maps not found. This indicates incomplete project setup. ' +
          'Please return to Project Setup and ensure all foundation data is saved correctly. ' +
          '(Technical: dateElements.dateRecordsMapByPaymentDate is required)'
      );
    }

    if (!projectData.dateElements?.dateRecordsMapByTaxYear) {
      throw new Error(
        'Date maps not found. This indicates incomplete project setup. ' +
          '(Technical: dateElements.dateRecordsMapByTaxYear is required)'
      );
    }
  }

  // Handle dateElements conversion - merge dateRecords if needed
  const dateElements: ProjectDateElements | undefined = projectData.dateElements
    ? {
        ...projectData.dateElements,
        dateRecords: projectData.dateRecords || projectData.dateElements.dateRecords || null,
      }
    : projectData.dateRecords
      ? {
          dateRecords: projectData.dateRecords,
          dateRecordsMapByTaxYear: undefined,
          dateRecordsMapByPaymentDate: undefined,
        }
      : undefined;

  return {
    id: projectData.metadata.id,
    name: projectData.metadata.name,
    dateInputs: projectData.dateInputs,
    taxValueInputs: projectData.taxValueInputs,
    existingDebtInputs: projectData.existingDebtInputs,
    millageInputs: projectData.millageInputs,
    drfInputs: projectData.drfInputs,
    bondInterestRateSettings: projectData.bondInterestRateSettings,
    taxableValueMap: projectData.taxableValueMap,
    existingDebtProjects: projectData.existingDebtProjects,
    dateElements: dateElements,
    configurations: projectData.configurations,
    lastUpdated: projectData.metadata.updatedAt,
    processingStatus: 'complete',
  };
};
